// How the admin app names a model. A person reads "Opus 5.5", not `us.anthropic.claude-opus-5-5`
// (the no-raw-ids rule): the label is the catalog's display name without the vendor's family word,
// the hover title the full name and the id. The API sends no model catalog to the admin, so the
// proxy's model map is copied here; tests/unit/model-names.test.ts checks the copy against
// proxy/nexus_plugin/model_map.default.json. A model the proxy serves through a discovered
// inference profile (nexus_plugin/models.py, `discovered`) is not in that map, so the ids seen
// in service are named in DISCOVERED_MODELS until the map gains them. An id neither knows is cut
// into words.

/** Bedrock model id → the catalog's display name (proxy/nexus_plugin/model_map.default.json). */
export const MODEL_CATALOG: Readonly<Record<string, string>> = {
  "us.anthropic.claude-fable-5-1": "Claude Fable 5.1",
  "us.anthropic.claude-fable-5": "Claude Fable 5",
  "us.anthropic.claude-opus-5-5": "Claude Opus 5.5",
  "us.anthropic.claude-opus-5": "Claude Opus 5",
  "us.anthropic.claude-sonnet-5": "Claude Sonnet 5",
  "us.anthropic.claude-opus-4-8": "Claude Opus 4.8",
  "us.anthropic.claude-opus-4-7": "Claude Opus 4.7",
  "us.anthropic.claude-opus-4-6-v1": "Claude Opus 4.6",
  "us.anthropic.claude-sonnet-4-6": "Claude Sonnet 4.6",
  "us.anthropic.claude-opus-4-5-20251101-v1:0": "Claude Opus 4.5",
  "us.anthropic.claude-opus-4-1-20250805-v1:0": "Claude Opus 4.1",
  "us.anthropic.claude-sonnet-4-5-20250929-v1:0": "Claude Sonnet 4.5",
  "us.anthropic.claude-sonnet-4-20250514-v1:0": "Claude Sonnet 4",
  "us.anthropic.claude-haiku-4-5-20251001-v1:0": "Claude Haiku 4.5",
  "us.openai.gpt-6-astra": "GPT-6 Astra",
  "us.openai.gpt-6-sol": "GPT-6 Sol",
  "us.openai.gpt-6-luna": "GPT-6 Luna",
  "us.openai.gpt-5.6-sol": "GPT-5.6 Sol",
  "us.openai.gpt-5.6-terra": "GPT-5.6 Terra",
  "us.openai.gpt-5.6-luna": "GPT-5.6 Luna",
  "us.openai.gpt-5.5": "GPT-5.5",
  "us.openai.gpt-5.4": "GPT-5.4",
  "openai.gpt-oss-120b-1:0": "gpt-oss-120b",
  "openai.gpt-oss-20b-1:0": "gpt-oss-20b",
};

/**
 * Bedrock ids the proxy has served through a discovered inference profile, not from the map, with
 * the catalog-style full name they read as. The same model under two region prefixes (`us.`,
 * `global.`) is two ids here, as it is two series and two rows everywhere the API keys by id.
 */
export const DISCOVERED_MODELS: Readonly<Record<string, string>> = {
  "us.anthropic.claude-sonnet-5-5": "Claude Sonnet 5.5",
  "global.anthropic.claude-sonnet-5-5": "Claude Sonnet 5.5",
};

export const NO_MODEL = "No model";
const NO_MODEL_TITLE = "The request was refused before it reached a model";

/** The vendor family word the short label drops: "Claude Opus 5.5" reads "Opus 5.5". */
const FAMILY_PREFIX = /^Claude\s+/;

/** The short name a chart legend or a table cell shows: "Opus 5.5", "GPT-5.6 Sol". */
export function modelDisplayName(bedrockModelId: string): string {
  return modelFullName(bedrockModelId).replace(FAMILY_PREFIX, "");
}

/** The catalog's full name ("Claude Opus 5.5"), else the readable fallback. */
export function modelFullName(bedrockModelId: string): string {
  const id = bedrockModelId.trim();
  if (id === "") return NO_MODEL;
  return MODEL_CATALOG[id] ?? DISCOVERED_MODELS[id] ?? fallbackName(id);
}

/** The label and the hover title together, for a cell that shows one and carries the other. */
export function modelLabel(bedrockModelId: string): { label: string; title: string } {
  const id = bedrockModelId.trim();
  if (id === "") return { label: NO_MODEL, title: NO_MODEL_TITLE };
  return { label: modelDisplayName(id), title: `${modelFullName(id)} · ${id}` };
}

// An id is `[region.]provider.model[-YYYYMMDD][-vN[:M]]`: `us.anthropic.claude-haiku-9-20270101-v1:0`.
/** A short lowercase segment ahead of the provider's ("us.", "global.", any future one); the provider's own segment has no dot after it. */
const REGION_PREFIX = /^[a-z]{1,8}\.(?=[a-z]+\.)/;
const PROVIDER_PREFIX = /^(anthropic\.claude-|anthropic\.|openai\.|amazon\.|meta\.|mistral\.|cohere\.|ai21\.|stability\.|deepseek\.|writer\.|luma\.|qwen\.)/;
const DATE_SUFFIX = /-\d{8}(?=(-v\d+(:\d+)?)?$)/;
const VERSION_SUFFIX = /-v\d+(:\d+)?$/;
/** A bare version number segment ("1:0") left by a provider id that carries no `-v`. */
const BARE_VERSION_SUFFIX = /-\d+:\d+$/;
/** Acronyms stay upper-case; "gpt" keeps its hyphen to the version that follows ("GPT-7"). */
const ACRONYMS = new Set(["gpt"]);
/** One segment of a dotted version an id spells with hyphens: the "5" and "5" of `sonnet-5-5`. */
const VERSION_SEGMENT = /^\d+$/;

/** The id without its region, provider and version, its remaining words title-cased ("Haiku 9", "GPT-7 Oss", "Foo 5.5"). */
function fallbackName(id: string): string {
  const core = id.replace(REGION_PREFIX, "").replace(PROVIDER_PREFIX, "").replace(DATE_SUFFIX, "").replace(VERSION_SUFFIX, "").replace(BARE_VERSION_SUFFIX, "");
  const words = core.split("-").filter((word) => word !== "");
  return joinVersions(words).map(titleWord).join(" ");
}

/**
 * "foo", "5", "5" → "foo", "5.5": a run of number segments is one dotted version; "gpt", "5", "6" →
 * "gpt-5.6": an acronym rejoins the version after it. "llama3", "3" stays apart: a word that only
 * ends in a digit is a name, not a version.
 */
function joinVersions(words: readonly string[]): string[] {
  const out: string[] = [];
  let previousIsVersion = false;
  for (const word of words) {
    const previous = out[out.length - 1];
    const isVersion = VERSION_SEGMENT.test(word);
    if (previous !== undefined && isVersion && previousIsVersion) out[out.length - 1] = `${previous}.${word}`;
    else if (previous !== undefined && isVersion && ACRONYMS.has(previous.toLowerCase())) out[out.length - 1] = `${previous}-${word}`;
    else out.push(word);
    previousIsVersion = isVersion;
  }
  return out;
}

/** "gpt-7" → "GPT-7"; "haiku" → "Haiku". */
function titleWord(word: string): string {
  const [head = "", ...rest] = word.split("-");
  if (ACRONYMS.has(head.toLowerCase())) return [head.toUpperCase(), ...rest].join("-");
  const lower = word.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
